import { CheckCheck, RotateCcw, Trash2, UserRound, X } from 'lucide-react';
import type { BulkTaskInput } from '@peles/shared';
import { Popover } from '../common/Popover';
import { DueDatePicker } from './DueDatePicker';
import type { TaskAssignee } from './taskUi';

export type BulkChange = Omit<BulkTaskInput, 'itemIds'>;

export interface TaskBulkBarProps {
  count: number;
  onApply: (change: BulkChange) => void;
  onClear: () => void;
  assignees: TaskAssignee[];
  todayKey: string;
  busy?: boolean;
}

const buttonClass =
  'inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm rounded-md text-gray-700 hover:bg-gray-100 disabled:opacity-50';

/** What can be done to several tasks at once — shown while any are selected. */
export function TaskBulkBar({
  count,
  onApply,
  onClear,
  assignees,
  todayKey,
  busy,
}: TaskBulkBarProps) {
  if (count === 0) return null;
  return (
    <div
      role="toolbar"
      aria-label="Selected tasks"
      className="sticky bottom-3 z-10 mx-auto flex flex-wrap items-center gap-1 rounded-xl border border-gray-200 bg-white px-3 py-2 shadow-lg"
    >
      <span className="text-sm font-medium text-gray-900 mr-2">{count} selected</span>
      <button
        type="button"
        className={buttonClass}
        disabled={busy}
        onClick={() => onApply({ action: 'complete' })}
      >
        <CheckCheck size={15} /> Mark done
      </button>
      <button
        type="button"
        className={buttonClass}
        disabled={busy}
        onClick={() => onApply({ action: 'reopen' })}
      >
        <RotateCcw size={15} /> Reopen
      </button>
      <Popover
        ariaLabel="Assign selected tasks"
        panelClassName="w-56 p-1 max-h-72 overflow-y-auto"
        trigger={(props) => (
          <button type="button" {...props} className={buttonClass} disabled={busy}>
            <UserRound size={15} /> Assign
          </button>
        )}
      >
        {(close) => (
          <ul>
            {[{ id: '', name: 'Nobody' }, ...assignees].map((a) => (
              <li key={a.id || 'nobody'}>
                <button
                  type="button"
                  onClick={() => {
                    onApply({ action: 'assign', assignedToUserId: a.id || null });
                    close();
                  }}
                  className="w-full text-left px-3 py-1.5 text-sm rounded hover:bg-gray-100"
                >
                  {a.name}
                </button>
              </li>
            ))}
          </ul>
        )}
      </Popover>
      <DueDatePicker
        value=""
        onChange={(value) => onApply({ action: 'due', dueDate: value || null })}
        todayKey={todayKey}
        placeholder="Set due date"
        className="border-0 hover:bg-gray-100"
      />
      <button
        type="button"
        className={`${buttonClass} text-red-600 hover:bg-red-50`}
        disabled={busy}
        onClick={() => onApply({ action: 'delete' })}
      >
        <Trash2 size={15} /> Delete
      </button>
      <button
        type="button"
        onClick={onClear}
        aria-label="Clear selection"
        className="ml-auto p-1.5 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100"
      >
        <X size={16} />
      </button>
    </div>
  );
}
